import React, { useState } from "react";
import {
  Box,
  Typography,
  Modal,
  Divider,
  Chip,
  IconButton,
  CircularProgress,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VerifiedIcon from "@mui/icons-material/Verified";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";

const ratingConfig = [
  { rating: 5, emoji: "😎", label: "Excellent", color: "#4caf50", bgColor: "#e8f5e9" },
  { rating: 4, emoji: "😀", label: "Very Good", color: "#8bc34a", bgColor: "#f1f8e9" },
  { rating: 3, emoji: "🙂", label: "Good", color: "#ff9800", bgColor: "#fff3e0" },
  { rating: 2, emoji: "😐", label: "Poor", color: "#f44336", bgColor: "#fbe9e7" },
  { rating: 1, emoji: "😡", label: "Very Poor", color: "#d32f2f", bgColor: "#ffebee" },
];

const getRatingInfo = (value) => {
  const numVal = Number(value);
  if (!numVal || numVal < 1 || numVal > 5) return null;
  return ratingConfig.find((r) => r.rating === numVal) || null;
};

const FeedbackRatingCard = ({ label, value, selectedRating }) => {
  const info = getRatingInfo(selectedRating);
  if (!info && !value) return null;

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        backgroundColor: info ? info.bgColor : "#f5f5f5",
        border: `1px solid ${info ? info.color + "40" : "#e0e0e0"}`,
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0 3px 10px rgba(0,0,0,0.06)",
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "#666",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          fontSize: "0.65rem",
          mb: 0.5,
          display: "block",
        }}
      >
        {label}
      </Typography>
      {info ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "1.5rem", lineHeight: 1 }}>{info.emoji}</Typography>
          <Box>
            <Typography sx={{ fontWeight: 700, color: info.color, fontSize: "0.85rem" }}>
              {info.label}
            </Typography>
            <Typography variant="caption" sx={{ color: "#999", fontSize: "0.65rem" }}>
              Rating: {info.rating}/5
            </Typography>
          </Box>
        </Box>
      ) : (
        <Typography sx={{ fontSize: "0.85rem", color: "#333", fontStyle: "italic" }}>
          {value || "N/A"}
        </Typography>
      )}
    </Box>
  );
};

const GrievanceModal = ({ open, onClose, modalData, modalLoader, feedbackData, selectedRating }) => {
  console.log({ feedbackData });
  const [saved, setSaved] = useState(0);

  if (!modalData) return null;

  const isClosed =
    modalData.complaintStatus?.toLowerCase() === "dispose off" ||
    modalData.companyStatus?.toLowerCase() === "closed";

  const cleanedComplaintDetails = modalData.grievanceDetails
    ?.replace(/\r\n|\n|\r/g, "\n")
    ?.trim();

  // Helper to validate if value exists and is not N/A
  const valid = (val) => {
    if (val === null || val === undefined) return false;
    const str = typeof val === "string" ? val.trim() : String(val).trim();
    return str !== "" && str.toLowerCase() !== "n/a";
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const companyName = valid(modalData.converganceCompanyName)
    ? modalData.converganceCompanyName
    : valid(modalData.nonCoverganeceCompanyName)
      ? modalData.nonCoverganeceCompanyName
      : "";

  const isConvergence = valid(modalData.converganceCompanyName);

  const userName = [modalData.firstName, modalData.lastName].filter(Boolean).join(" ").trim();

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          width: { xs: "95%", md: "85%" },
          height: "90vh",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {modalLoader ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Header - Complaint # | Company | Status | Close */}
            <Box sx={{ p: 3, pb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 3,
                }}
              >
                {/* Complaint # */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Complaint #{modalData.complainNumber}
                  </Typography>
                  {valid(modalData.referenceId) && (
                    <Typography variant="caption" sx={{ color: "primary.main" }}>
                      Ref: {modalData.referenceId}
                    </Typography>
                  )}
                </Box>

                <Typography variant="body2" sx={{ color: "text.secondary" }}>•</Typography>

                {/* Company Name */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {companyName && (
                    <Typography variant="h7" sx={{ fontWeight: 600 }}>
                      {companyName}
                    </Typography>
                  )}
                  {(isConvergence || valid(modalData.nonCoverganeceCompanyName)) && (
                    <Chip
                      label={isConvergence ? "Convergence" : "Non-Convergence"}
                      size="small"
                      sx={{
                        fontSize: "0.65rem",
                        height: "20px",
                        backgroundColor: isConvergence ? "#e8f5e9" : "#fff3e0",
                        color: isConvergence ? "#2e7d32" : "#e65100",
                        fontWeight: 600,
                        borderRadius: "4px",
                      }}
                    />
                  )}
                </Box>

                <Typography variant="body2" sx={{ color: "text.secondary" }}>•</Typography>

                {/* Status */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {valid(modalData.grievanceStatus) && (
                    <Chip
                      icon={isClosed ? <VerifiedIcon sx={{ fontSize: "0.9rem" }} /> : undefined}
                      label={modalData.grievanceStatus}
                      color={isClosed ? "success" : "warning"}
                      size="small"
                      sx={{ fontWeight: 600, textTransform: "capitalize" }}
                    />
                  )}
                  {valid(modalData.companyStatus) && (
                    <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
                      (Company: {modalData.companyStatus})
                    </Typography>
                  )}
                  {isClosed && (
                    <VerifiedIcon sx={{ color: "green" }} fontSize="small" />
                  )}
                </Box>

                {/* Close button pushed to far right */}
                <IconButton
                  onClick={onClose}
                  size="small"
                  sx={{
                    ml: "auto",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            <Divider />

            {/* Body */}
            <Box
              sx={{
                display: "flex",
                flex: 1,
                overflow: "hidden",
                p: { xs: 2, md: 3 },
                gap: 2,
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              {/* ═══ LEFT SIDE — User Info + Complaint Details ═══ */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  pr: { md: 2 },
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {/* User Details Card */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#f8f9fc",
                    border: "1px solid #e8e8ee",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, mb: 1.5, color: "#444", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px" }}
                  >
                    Complainant
                  </Typography>

                  {/* Name + Avatar */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: "#5c6bc0",
                        fontSize: "0.85rem",
                        fontWeight: 700,
                      }}
                    >
                      {userName ? userName.charAt(0).toUpperCase() : "U"}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                        {userName || "N/A"}
                      </Typography>
                      {valid(modalData.userId) && (
                        <Typography variant="caption" sx={{ color: "#999", fontSize: "0.7rem" }}>
                          ID: {modalData.userId}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Contact Info with icons */}
                  {valid(modalData.emailId) && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8 }}>
                      <EmailOutlinedIcon sx={{ fontSize: "0.95rem", color: "#888" }} />
                      <Typography sx={{ fontSize: "0.88rem" }}>{modalData.emailId}</Typography>
                    </Box>
                  )}
                  {valid(modalData.mbileNumber) && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8 }}>
                      <PhoneOutlinedIcon sx={{ fontSize: "0.95rem", color: "#888" }} />
                      <Typography sx={{ fontSize: "0.88rem" }}>{modalData.mbileNumber}</Typography>
                    </Box>
                  )}
                  {(valid(modalData.stateName) || valid(modalData.purchaseCity)) && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LocationOnOutlinedIcon sx={{ fontSize: "0.95rem", color: "#888" }} />
                      <Typography sx={{ fontSize: "0.88rem" }}>
                        {[modalData.purchaseCity, modalData.stateName].filter((v) => valid(v)).join(", ")}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Complaint Meta */}
                <Box>
                  {valid(modalData.complaintRegDate) && (
                    <Typography sx={{ fontSize: "0.9rem", mb: 0.5 }}>
                      <strong>Date:</strong>{" "}
                      {new Date(modalData.complaintRegDate).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </Typography>
                  )}
                  {valid(modalData.sectorName) && (
                    <Typography sx={{ fontSize: "0.9rem", mb: 0.5 }}>
                      <strong>Sector:</strong> {modalData.sectorName}
                    </Typography>
                  )}
                  {valid(modalData.categoryName) && (
                    <Typography sx={{ fontSize: "0.9rem", mb: 0.5 }}>
                      <strong>Category:</strong> {modalData.categoryName}
                    </Typography>
                  )}
                  {valid(modalData.grievanceClassification) && (
                    <Typography sx={{ fontSize: "0.9rem", mb: 0.5 }}>
                      <strong>Classification:</strong> {modalData.grievanceClassification}
                    </Typography>
                  )}
                  {valid(modalData.productValue) && (
                    <Typography sx={{ fontSize: "0.9rem", mb: 0.5 }}>
                      <strong>Product Value:</strong> {modalData.productValue}
                    </Typography>
                  )}
                  {valid(modalData.companyGrievanceNo) && (
                    <Typography sx={{ fontSize: "0.9rem", mb: 0.5 }}>
                      <strong>Company Grievance No:</strong> {modalData.companyGrievanceNo}
                    </Typography>
                  )}
                </Box>

                {/* Subject */}
                {valid(modalData.frequentlyOccuredProblem) && (
                  <Box>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>
                      Subject:
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.95rem", lineHeight: 1.6 }}
                    >
                      {modalData.frequentlyOccuredProblem}
                    </Typography>
                  </Box>
                )}

                {/* Complaint Details */}
                {valid(cleanedComplaintDetails) && (
                  <Box>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>
                      Details:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.95rem",
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.8,
                      }}
                    >
                      {cleanedComplaintDetails}
                    </Typography>
                  </Box>
                )}

                {/* Agency Details */}
                {valid(modalData.agencyDetails) && (
                  <Box>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>
                      Agency Details:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.88rem",
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.7,
                        color: "#555",
                      }}
                    >
                      {modalData.agencyDetails}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* ═══ RIGHT SIDE — Feedback, Agent Remark, Timeline ═══ */}
              <Divider
                orientation="vertical"
                flexItem
                sx={{ display: { xs: "none", md: "block" } }}
              />

              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  pl: { xs: 0, md: 3 },
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {/* Status Timeline */}
                <Box>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>
                    Timeline
                  </Typography>

                  <Timeline
                    position="right"
                    sx={{
                      p: 0,
                      mt: 1,
                      "& .MuiTimelineItem-root:before": {
                        flex: 0,
                        padding: 0,
                      },
                    }}
                  >
                    {valid(modalData.complaintRegDate) && (
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot color="primary" />
                          <TimelineConnector />
                        </TimelineSeparator>
                        <TimelineContent sx={{ mt: -0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(
                              modalData.complaintRegDate
                            ).toLocaleDateString("en-IN")}
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Registered
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {valid(modalData.complaintMode) && (
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Typography variant="body2">From</Typography>
                                <Chip
                                  label={modalData.complaintMode}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </Box>
                            )}
                            {valid(modalData.grievanceClassification) && (
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Typography variant="body2">Under</Typography>
                                <Chip
                                  label={modalData.grievanceClassification}
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                />
                              </Box>
                            )}
                          </Box>
                        </TimelineContent>
                      </TimelineItem>
                    )}

                    {valid(modalData.lastUpdationDate) &&
                      modalData.lastUpdationDate !== modalData.complaintRegDate && (
                        <TimelineItem>
                          <TimelineSeparator>
                            <TimelineDot sx={{ bgcolor: "#ff9800" }} />
                            {isClosed && <TimelineConnector />}
                          </TimelineSeparator>
                          <TimelineContent sx={{ mt: -0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(
                                modalData.lastUpdationDate
                              ).toLocaleDateString("en-IN")}
                            </Typography>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 600 }}
                            >
                              Last Updated
                            </Typography>
                            {valid(modalData.lastEditedBy) && (
                              <Typography variant="body2" color="text.secondary">
                                User ID: {modalData.lastEditedBy}
                              </Typography>
                            )}
                          </TimelineContent>
                        </TimelineItem>
                      )}

                    {isClosed && (
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot color="success" />
                        </TimelineSeparator>
                        <TimelineContent sx={{ mt: -0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(
                              modalData.lastUpdationDate
                            ).toLocaleDateString("en-IN")}
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {modalData.complaintStatus}
                          </Typography>
                          {valid(modalData.companyStatus) && (
                            <Typography variant="body2" color="text.secondary">
                              Company Status: {modalData.companyStatus}
                            </Typography>
                          )}
                        </TimelineContent>
                      </TimelineItem>
                    )}
                  </Timeline>
                </Box>

                <Divider />

                {/* Agent Remark */}
                {valid(modalData.agentRemark) && (
                  <Box>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>
                      Agent Remark:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.95rem",
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.8,
                      }}
                    >
                      {modalData.agentRemark}
                    </Typography>
                  </Box>
                )}

                {/* User Feedback */}
                <Box>
                  <Typography sx={{ fontWeight: 600, mb: 0.2 }}>
                    User Feedback:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.95rem",
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.8,
                    }}
                  >
                    {
                      [
                        modalData.userexperience && truncateText(modalData.userexperience),

                        modalData.ExperiencewithNCH &&
                        (modalData.ExperiencewithNCH.toLowerCase() === "unsatisfactory"
                          ? truncateText(modalData.unUnsatisfactory)
                          : truncateText(modalData.ExperiencewithNCH)),

                        modalData.Remark && truncateText(modalData.Remark, 150)
                      ]
                        .filter(Boolean)
                        .join(" | ")
                    }
                  </Typography>
                </Box>

                {/* Feedback Ratings from get_feedback_by_docket */}
                {feedbackData && Array.isArray(feedbackData) && feedbackData.length > 0 && (
                  <Box>
                    <Divider sx={{ my: 1.5 }} />
                    {feedbackData.map((fb, idx) => (
                      <Box key={idx} sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: idx < feedbackData.length - 1 ? 2 : 0 }}>
                        {feedbackData.length > 1 && (
                          <Typography variant="caption" sx={{ color: "#888", fontWeight: 600, fontSize: "0.7rem" }}>
                            Entry #{idx + 1}
                          </Typography>
                        )}
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                          <FeedbackRatingCard label="Experience with NCH" value={fb.ExperiencewithNCH?.toLowerCase() === "unsatisfactory" ? fb.unUnsatisfactory : fb.ExperiencewithNCH} selectedRating={selectedRating} />
                        </Box>
                        {idx < feedbackData.length - 1 && <Divider />}
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Government Department */}
                {valid(modalData.govtDepartment) && (
                  <>
                    <Divider />
                    <Box>
                      <Typography sx={{ fontWeight: 600, mb: 1 }}>
                        Government Department:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.95rem",
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.8,
                        }}
                      >
                        {modalData.govtDepartment}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default GrievanceModal;